import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts';

import currentTimeZone from '../../../../config/timeZone';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import type { RootState } from '../../../../redux/store';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import stationManagement from '../../../../utils/api/location-management';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

/**
 * Color palette for chart datasets
 */
const COLOR_PALETTE = ['#00b894', '#6c5ce7', '#d63031', '#0984e3'];

interface StationUptimeEntry {
  chargingStation: string;
  stationName: string;
  avgOnlineHours: number;
}

interface ChartDataProperties {
  date: string;
  stations?: StationUptimeEntry[];
  hour?: number | string;
  time?: string;
  day?: number | string;
  month?: number | string;
  monthName?: string;
  year?: number | string;
  week?: number | string;
  weekName?: string;
  label?: string;
  [key: string]: any;
}

/**
 * Month translation mapping dictionary.
 */
const monthMap: Record<string, string> = {
  Jan: 'jan',
  Feb: 'feb',
  Mar: 'mar',
  Apr: 'apr',
  May: 'may_month',
  Jun: 'jun',
  Jul: 'jul',
  Aug: 'aug',
  Sep: 'sep',
  Oct: 'oct',
  Nov: 'nov',
  Dec: 'dec',
};

/**
 * Short month names array for month resolution.
 */
const shortMonths: string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Formats a chart label based on whether it represents an hour, day/date, week, or month.
 * @param {ChartDataProperties} item - The chart data item.
 * @param {any} t - The translation function.
 * @param {string} [locale] - Current active locale.
 * @param {string} [startDate] - Optional start date of current range.
 * @param {string} [endDate] - Optional end date of current range.
 * @returns {string} Formatted label string for the chart axis.
 */
const formatChartLabel = (
  item: ChartDataProperties,
  t: any,
  locale: string = 'en',
  startDate?: string,
  endDate?: string,
): string => {
  // 1. Hour / Time format handling (e.g. { hour: 10, time: "10:00", date: "2026-08-31" })
  if (item.time !== undefined && item.time !== null && item.time !== '') {
    return String(item.time).trim().slice(0, 5);
  }
  if (item.hour !== undefined && item.hour !== null && item.hour !== '') {
    const hourNumber = Number(item.hour);
    if (!Number.isNaN(hourNumber)) {
      return `${String(hourNumber).padStart(2, '0')}:00`;
    }
    return String(item.hour).trim();
  }

  // 2. Specific Date / Day handling (takes priority over standalone month)
  let parsedDate: Date | null = null;
  const rawDate = item.date;

  if (rawDate !== undefined && rawDate !== null && rawDate !== '') {
    const dateString = String(rawDate).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const [yearString, monthPart, dayString] = dateString.split('T')[0].split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [dayString, monthPart, yearString] = dateString.split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (dateString.includes('-') || dateString.includes('/')) {
      const generalDate = new Date(dateString);
      if (!Number.isNaN(generalDate.getTime())) {
        parsedDate = generalDate;
      }
    }
  }

  // If parsed date was not found via rawDate, check day + month numbers
  if (!parsedDate && item.day !== undefined && item.day !== null && item.day !== '') {
    const dayNumber = Number(item.day);
    const rawMonthValue = item.month ?? item.monthName;
    const monthNumber =
      rawMonthValue !== undefined && rawMonthValue !== null && rawMonthValue !== ''
        ? Number(rawMonthValue)
        : NaN;

    if (!Number.isNaN(dayNumber) && dayNumber >= 1 && dayNumber <= 31) {
      let resolvedMonth =
        !Number.isNaN(monthNumber) && monthNumber >= 1 && monthNumber <= 12 ? monthNumber : NaN;

      if (Number.isNaN(resolvedMonth) && typeof rawMonthValue === 'string') {
        const monthIndex = shortMonths.findIndex(
          (m) => m.toLowerCase() === rawMonthValue.trim().slice(0, 3).toLowerCase(),
        );
        if (monthIndex !== -1) {
          resolvedMonth = monthIndex + 1;
        }
      }

      if (!Number.isNaN(resolvedMonth)) {
        const fallbackYear = startDate
          ? new Date(startDate).getFullYear()
          : new Date().getFullYear();
        const yearNumber = Number(item.year) || fallbackYear;
        const dateObject = new Date(yearNumber, resolvedMonth - 1, dayNumber);
        if (!Number.isNaN(dateObject.getTime())) {
          parsedDate = dateObject;
        }
      }
    }
  }

  if (parsedDate) {
    const isMultiYear =
      Boolean(startDate) &&
      Boolean(endDate) &&
      new Date(startDate!).getFullYear() !== new Date(endDate!).getFullYear();

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      ...(isMultiYear ? { year: '2-digit' } : {}),
    }).format(parsedDate);
  }

  // 3. Week format handling
  const weekValue = item.weekName || item.week;
  if (weekValue !== undefined && weekValue !== null && weekValue !== '') {
    const weekString = String(weekValue).trim();
    const weekMatch = weekString.match(/^week\s*(\d+)$/i) || weekString.match(/^w\s*(\d+)$/i);
    if (weekMatch) {
      return `${t('week', { defaultValue: 'Week' })} ${weekMatch[1]}`;
    }
    if (/^\d+$/.test(weekString)) {
      return `${t('week', { defaultValue: 'Week' })} ${weekString}`;
    }
    return weekString;
  }

  // 4. Month format handling (monthly aggregate without day)
  const rawMonth = item.monthName || item.month;
  if (rawMonth !== undefined && rawMonth !== null && rawMonth !== '') {
    const monthString = String(rawMonth).trim();

    // Check numeric month 1-12 or "01"-"12"
    if (/^\d{1,2}$/.test(monthString)) {
      const monthNumber = Number(monthString);
      if (monthNumber >= 1 && monthNumber <= 12) {
        const fallbackYear = startDate
          ? new Date(startDate).getFullYear()
          : new Date().getFullYear();
        const yearNumber = Number(item.year) || fallbackYear;
        const dateObject = new Date(yearNumber, monthNumber - 1, 1);
        const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
        return item.year ? `${monthShort} ${item.year}` : monthShort;
      }
    }

    // Check "Jan 2024" or "Jan 24" format
    const parts = monthString.split(' ');
    if (parts.length === 2 && monthMap[parts[0]]) {
      return `${t(monthMap[parts[0]])} ${parts[1]}`;
    }

    // Check "Jan", "Feb" format
    if (monthMap[monthString]) {
      return t(monthMap[monthString]);
    }

    // Check "YYYY-MM" format e.g. "2026-08"
    if (/^\d{4}-\d{2}$/.test(monthString)) {
      const [year, month] = monthString.split('-').map(Number);
      const dateObject = new Date(year, month - 1, 1);
      if (!Number.isNaN(dateObject.getTime())) {
        const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
        return `${monthShort} ${year}`;
      }
    }

    return monthString;
  }

  // 5. Fallback on label
  if (item.label !== undefined && item.label !== null && item.label !== '') {
    const labelString = String(item.label).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(labelString)) {
      const [yearString, monthPart, dayString] = labelString.split('T')[0].split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        return new Intl.DateTimeFormat(locale, {
          day: '2-digit',
          month: 'short',
        }).format(dateObject);
      }
    }
    if (/^\d{1,2}$/.test(labelString)) {
      const numericMonth = Number(labelString);
      if (numericMonth >= 1 && numericMonth <= 12) {
        const dateObject = new Date(2000, numericMonth - 1, 1);
        return new Intl.DateTimeFormat(locale, { month: 'short' }).format(dateObject);
      }
    }
    if (monthMap[labelString]) {
      return t(monthMap[labelString]);
    }
    return labelString;
  }

  return '';
};

/**
 * Formats a detailed title string for the chart tooltip.
 * @param {ChartDataProperties} item - The chart data item.
 * @param {any} t - The translation function.
 * @param {string} [locale] - Current active locale.
 * @returns {string} Formatted tooltip title string.
 */
const getTooltipTitle = (item: ChartDataProperties, t: any, locale: string = 'en'): string => {
  // 1. Time / Hour with date
  if (item.date && item.time) {
    return `${item.date} ${item.time}`;
  }
  if (item.date && item.hour !== undefined && item.hour !== null && item.hour !== '') {
    const hourNumber = Number(item.hour);
    const hourPad = !Number.isNaN(hourNumber)
      ? `${String(hourNumber).padStart(2, '0')}:00`
      : String(item.hour);
    return `${item.date} ${hourPad}`;
  }

  // 2. Specific date
  if (item.date && /^\d{4}-\d{2}-\d{2}/.test(String(item.date))) {
    const [yearString, monthPart, dayString] = String(item.date).split('T')[0].split('-');
    const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
    if (!Number.isNaN(dateObject.getTime())) {
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  // 3. Day + Month
  if (item.day !== undefined && item.day !== '' && item.month !== undefined && item.month !== '') {
    const d = Number(item.day);
    const m = Number(item.month);
    const y = Number(item.year) || new Date().getFullYear();
    if (!Number.isNaN(d) && !Number.isNaN(m) && m >= 1 && m <= 12) {
      const dateObject = new Date(y, m - 1, d);
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  // 4. Month with Year
  if (item.month && item.year) {
    const m = Number(item.month);
    const y = Number(item.year);
    if (!Number.isNaN(m) && !Number.isNaN(y) && m >= 1 && m <= 12) {
      const dateObject = new Date(y, m - 1, 1);
      return new Intl.DateTimeFormat(locale, {
        month: 'long',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  return item.label || '';
};

/**
 * Uptime Trend Over Time Chart component.
 * Displays station uptime trends over a date range using Chart.js Line chart.
 * @returns {React.JSX.Element} The rendered component.
 */
function UptimeTrendOverTimeChart(): React.JSX.Element {
  const { t, i18n } = useTranslation('sidePanel');
  const {
    selectedStationIds,
    dateRange: comparisonDateRange,
    isFetchingLocationDetails,
  } = useAppSelector((state: RootState) => state.locationComparison);
  const dispatch = useAppDispatch();
  const [graphData, setGraphData] = useState<ChartDataProperties[]>([]);
  const currentDate = new Date().toISOString().split('T')[0];
  const startDate = comparisonDateRange?.startDate || currentDate;
  const endDate = comparisonDateRange?.endDate || currentDate;
  const [loading, setLoading] = useState<boolean>(false);
  const activeRequestReference = useRef<string>('');
  const chargingStationList = useAppSelector(
    (state: RootState) => state.locationManagement.chargingStationList,
  );

  const chartOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            /**
             * Hides all legend items (no radio buttons).
             * @returns {boolean} False to hide all items.
             */
            filter: () => false,
          },
        },
        tooltip: {
          backgroundColor: '#1F2937',
          titleColor: '#FFFFFF',
          bodyColor: '#FFFFFF',
          padding: 10,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            /**
             * Formats tooltip title showing full formatted date.
             * @param {any[]} contexts - Array of tooltip item contexts.
             * @returns {string} Formatted title string.
             */
            title: (contexts: any[]) => {
              if (!contexts.length) return '';
              const index = contexts[0].dataIndex;
              const item = graphData[index];
              if (item) {
                const formattedTitle = getTooltipTitle(item, t, i18n.language);
                if (formattedTitle) {
                  return formattedTitle;
                }
              }
              const rawDate = graphData[index]?.date;
              return rawDate && dayjs(rawDate).isValid()
                ? dayjs(rawDate).format('DD MMM YYYY')
                : contexts[0].label || '';
            },
            /**
             * Formats tooltip label showing station name and duration.
             * @param {any} context - Tooltip item context.
             * @returns {string} Formatted label string.
             */
            label: (context: any) => {
              const hours = Number(context.parsed?.y ?? context.raw ?? 0);
              const formattedDuration = FormatDuration(hours * 3600);
              return `${context.dataset?.label || t('station')}: ${formattedDuration}`;
            },
          },
        },
      },
      interaction: {
        intersect: false,
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            display: true,
            autoSkip: true,
            maxTicksLimit: 12,
            maxRotation: 45,
            minRotation: 0,
            color: '#6b7280',
            font: {
              size: 11,
              weight: 500,
            },
          },
        },
        y: {
          grid: { display: false }, // remove Y grid lines
          border: { display: false }, // remove Y axis line
          ticks: {
            display: true, // SHOW ticks
            count: 5,
            /**
             * Formats Y-axis tick value with hour unit.
             * @param {any} value - The tick value.
             * @returns {string} The formatted tick string.
             */
            callback: (value: any) => `${value} ${t('hrs')}`, // FORMAT ticks
            color: '#6b7280', // optional: nice gray
            font: {
              size: 11,
              weight: 500,
            },
          },
        },
      },
    }),
    [graphData, t, i18n.language],
  );

  /**
   * Fetches the uptime trend report data for the specified date range and updates the chart.
   * @param {string} fetchStartDate - The start date string.
   * @param {string} fetchEndDate - The end date string.
   * @returns {Promise<void>} A promise resolving when data is fetched.
   */
  const fetchReport = async (fetchStartDate: string, fetchEndDate: string): Promise<void> => {
    if (chargingStationList.length === 0) return;

    setLoading(true);

    const selected = selectedStationIds.filter(Boolean);
    const requestKey = `${fetchStartDate}_${fetchEndDate}_${selected.join(',')}`;
    activeRequestReference.current = requestKey;

    const payload: Record<string, any> = {
      startDate: fetchStartDate,
      endDate: fetchEndDate,
      timeZone: currentTimeZone,
    };

    if (selected.length > 0) {
      payload.chargingStations = selected;
    } else {
      payload.allStationList = true;
    }

    try {
      const response = await stationManagement.fetchUptimeOverTimeChartData(payload);

      // Discard response if a subsequent request has already been issued
      if (activeRequestReference.current !== requestKey) {
        return;
      }

      if (response.status === 200) {
        const data = response.data?.data || [];

        // Normalize raw items: extract Mongo _id subfields (day, month, year) if present
        const normalizedList = data.map((item: any) => {
          const idObject = item._id && typeof item._id === 'object' ? item._id : {};
          const day = item.day ?? idObject.day;
          const month = item.month ?? item.monthName ?? idObject.month;
          const year = item.year ?? idObject.year;

          let derivedDate = item.date;
          if (!derivedDate && day && month) {
            const y =
              year || (startDate ? new Date(startDate).getFullYear() : new Date().getFullYear());
            const m = String(month).padStart(2, '0');
            const d = String(day).padStart(2, '0');
            derivedDate = `${y}-${m}-${d}`;
          }

          return {
            ...item,
            day,
            month,
            year,
            date: derivedDate || item.date || item.createdAt || '',
          };
        });

        // Sort items chronologically
        normalizedList.sort((a: any, b: any) => {
          const dateA = a.date ? new Date(a.date).getTime() : NaN;
          const dateB = b.date ? new Date(b.date).getTime() : NaN;
          if (!Number.isNaN(dateA) && !Number.isNaN(dateB) && dateA !== dateB) {
            return dateA - dateB;
          }
          if (a.year !== undefined && b.year !== undefined && a.year !== b.year) {
            return Number(a.year) - Number(b.year);
          }
          if (a.month !== undefined && b.month !== undefined && a.month !== b.month) {
            return Number(a.month) - Number(b.month);
          }
          if (a.day !== undefined && b.day !== undefined && a.day !== b.day) {
            return Number(a.day) - Number(b.day);
          }
          if (a.hour !== undefined && b.hour !== undefined && a.hour !== b.hour) {
            return Number(a.hour) - Number(b.hour);
          }
          return 0;
        });

        setGraphData(normalizedList);
      }
    } catch (error: any) {
      if (activeRequestReference.current === requestKey) {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      }
    } finally {
      if (activeRequestReference.current === requestKey) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (chargingStationList.length === 0) return;

    fetchReport(startDate, endDate);
  }, [selectedStationIds, startDate, endDate, chargingStationList.length]);

  const labels = useMemo(
    () =>
      graphData.map((d) =>
        formatChartLabel(d, t, i18n.language, startDate, endDate),
      ),
    [graphData, t, i18n.language, startDate, endDate],
  );

  // Extract every unique station (keyed by id, not display name - two
  // stations can share a name, but never a chargingStation id).
  const { allStationIds, stationMetaById } = useMemo(() => {
    const metaMap = new Map<string, string>();
    graphData.forEach((d) => {
      (d.stations || []).forEach((s) => {
        if (!metaMap.has(s.chargingStation)) {
          metaMap.set(s.chargingStation, s.stationName);
        }
      });
    });
    return {
      allStationIds: Array.from(metaMap.keys()),
      stationMetaById: metaMap,
    };
  }, [graphData]);

  const datasets = useMemo(
    () =>
      allStationIds.map((chargingStation, index) => ({
        label: stationMetaById.get(chargingStation) || chargingStation,
        data: graphData.map((d) => {
          const station = d.stations?.find((s) => s.chargingStation === chargingStation);
          return Number(station?.avgOnlineHours ?? 0);
        }),
        borderColor: COLOR_PALETTE[index % COLOR_PALETTE.length],
        backgroundColor: `${COLOR_PALETTE[index % COLOR_PALETTE.length]}33`,
        fill: false,
        tension: 0.4,
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3,
      })),
    [allStationIds, graphData, stationMetaById],
  );

  return (
    <div className="uptime-trend-over-time-chart" dir={i18n.dir()}>
      <div className="uptime-trend-over-time-container">
        <div className="uptime-trend-over-time-header">
          <div className="uptime-trend-over-time-title">{t('uptimeTrendOverTime')}</div>
        </div>
        <div className="uptime-trend-over-time-container">
          <div className="uptime-trend-over-time-chart">
            <ResponsiveContainer width="100%" height={230}>
              {loading || isFetchingLocationDetails ? (
                <div className="loader">
                  <BatteryLoader />
                </div>
              ) : (
                <Line
                  data={{
                    labels,
                    datasets,
                  }}
                  options={chartOptions}
                />
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UptimeTrendOverTimeChart;
